// S5 — tenant detail, Overview (spec 15 §4). The header names the tenant and
// carries the lifecycle buttons this role may use in this state (lib/tenants.ts
// `tenantActions`); below it sit the facts, the admin invite, the suspension or
// setup panel when one applies, and the lifecycle timeline.
//
// While a tenant is setting up, the detail polls every two seconds so the
// step list moves on its own; it stops as soon as the tenant leaves
// `provisioning`. Whenever a read shows the status has moved, the history is
// read again, so the row that moved it appears under the header that shows it.
//
// Every lifecycle write holds its Idempotency-Key until the server answers it
// (lib/held-key.ts), so a retry after a lost answer replays rather than runs
// twice. A refusal says why: a role the server refused, or a tenant someone
// else moved first, in which case the tenant is read again.

import { enumLabel } from "../../../lib/enum-label";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { PageHeader } from "../../../components/breadcrumbs";
import { Button } from "../../../components/button";
import { Card } from "../../../components/card";
import { Field } from "../../../components/field";
import { Modal } from "../../../components/modal";
import { Select } from "../../../components/select";
import { CardSkeleton, EmptyState, ErrorState } from "../../../components/states";
import { StatusChip } from "../../../components/status-chip";
import { Banner } from "../../../components/toast";
import { formatTimestamp } from "../../../lib/datetime";
import { LOCALE_ENDONYMS } from "../../../lib/locale";
import { Fact, Facts } from "../components/facts";
import { HealthDot } from "../components/health-dot";
import { requestIdOf, useSignInAgainOn401 } from "../components/console-frame";
import {
  getTenant,
  listLifecycleEvents,
  listVerticalPacks,
  PlatformError,
  resendAdminInvite,
  restoreTenant,
  retryProvisioning,
  suspendTenant,
  type LifecycleEvent,
  type PlatformMe,
  type TenantDetail,
} from "../lib/platform-api";
import { packLabel } from "../lib/labels";
import { TenantTabs } from "../components/tenant-tabs";
import { EditTenantDialog, SubdomainDialog } from "../components/tenant-edit-dialogs";
import { canEditTenant } from "../lib/plans";
import { useHeldKey, withHeldKey, type HeldKey } from "../lib/held-key";
import { useActorName } from "../hooks/use-actor-name";
import {
  actionFailure,
  isProvisioningSettled,
  statusVariant,
  tenantActions,
  tenantOrigin,
  type SuspensionMode,
  type SuspensionReason,
} from "../lib/tenants";

export const TENANT_POLL_MS = 2_000;

export function tenantQueryKey(id: string) {
  return ["platform", "tenant", id] as const;
}

function lifecycleQueryKey(id: string) {
  return ["platform", "tenant", id, "lifecycle"] as const;
}

/** The banner for a refused lifecycle write (lib/tenants.ts `actionFailure`). */
export function actionMessage(error: unknown, fallback: string, t: TFunction): string {
  switch (actionFailure(error)) {
    case "scope":
      return t("admin.tenant.refusedScope");
    case "role":
      return t("admin.tenant.refusedRole");
    case "changed":
      return t("admin.tenant.changedFirst");
    default:
      return fallback;
  }
}

export function TenantDetailScreen({ me, tenantId }: { me: PlatformMe; tenantId: string }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const detail = useQuery({
    queryKey: tenantQueryKey(tenantId),
    queryFn: ({ signal }) => getTenant(tenantId, signal),
    refetchInterval: (query) => {
      const status = query.state.data?.tenant.status;
      return status !== undefined && !isProvisioningSettled(status) ? TENANT_POLL_MS : false;
    },
  });
  const packs = useQuery({
    queryKey: ["platform", "vertical-packs"],
    queryFn: ({ signal }) => listVerticalPacks(signal),
    staleTime: 5 * 60_000,
  });
  useSignInAgainOn401(detail.error);

  // A status the screen has not seen before means a lifecycle row it has not
  // read: the poll that watched setup finish, or a reload after a refusal.
  const status = detail.data?.tenant.status;
  const seenStatus = useRef(status);
  useEffect(() => {
    if (status === undefined) return;
    if (seenStatus.current !== undefined && seenStatus.current !== status) {
      void queryClient.invalidateQueries({ queryKey: lifecycleQueryKey(tenantId) });
      void queryClient.invalidateQueries({ queryKey: ["platform", "tenants"] });
    }
    seenStatus.current = status;
  }, [status, tenantId, queryClient]);

  const [dialog, setDialog] = useState<"suspend" | "restore" | "edit" | "subdomain" | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const retryKey = useHeldKey();
  const resendKey = useHeldKey();
  const suspendKey = useHeldKey();
  const restoreKey = useHeldKey();

  function settle(next: TenantDetail) {
    queryClient.setQueryData(tenantQueryKey(tenantId), next);
    void queryClient.invalidateQueries({ queryKey: lifecycleQueryKey(tenantId) });
    void queryClient.invalidateQueries({ queryKey: ["platform", "tenants"] });
  }

  /** After a refusal: if someone else moved the tenant, read it again. */
  function refused(error: unknown) {
    if (actionFailure(error) === "changed") {
      void queryClient.invalidateQueries({ queryKey: tenantQueryKey(tenantId) });
    }
  }

  const retry = useMutation({
    mutationFn: () =>
      withHeldKey(retryKey, { retry: tenantId }, (key) => retryProvisioning(tenantId, key)),
    onSuccess: settle,
    onError: refused,
  });
  const resend = useMutation({
    mutationFn: () =>
      withHeldKey(resendKey, { resend: tenantId }, (key) => resendAdminInvite(tenantId, key)),
    onSuccess: (next) => {
      settle(next);
      setNotice(t("admin.tenant.resent", { email: next.tenant.group_admin_email }));
    },
    onError: refused,
  });
  useSignInAgainOn401(retry.error);
  useSignInAgainOn401(resend.error);

  if (detail.isPending) {
    return (
      <section className="admin-screen" data-testid="admin-tenant-detail-loading">
        <PageHeader
          title={t("admin.tenant.loading")}
          crumbs={[{ label: t("admin.tenant.back"), to: "/console/tenants" }]}
        />
        <CardSkeleton lines={6} label={t("admin.tenant.loading")} />
      </section>
    );
  }

  if (detail.isError) {
    const notFound =
      detail.error instanceof PlatformError &&
      (detail.error.status === 404 || detail.error.status === 400);
    return (
      <section className="admin-screen" data-testid="admin-tenant-detail-error">
        <PageHeader
          title={notFound ? t("admin.tenant.notFoundTitle") : t("admin.load.failed")}
          crumbs={[{ label: t("admin.tenant.back"), to: "/console/tenants" }]}
        />
        {notFound ? (
          <EmptyState
            title={t("admin.tenant.notFoundTitle")}
            body={t("admin.tenant.notFoundBody")}
            ctaLabel={t("admin.tenant.back")}
            onCta={() => {
              void navigate({ to: "/console/tenants" });
            }}
          />
        ) : (
          <ErrorState
            title={t("admin.load.failed")}
            retryLabel={t("admin.load.retry")}
            onRetry={() => {
              void detail.refetch();
            }}
            requestId={requestIdOf(detail.error)}
            requestIdLabel={t("admin.load.requestId")}
          />
        )}
      </section>
    );
  }

  const view = detail.data;
  const tenant = view.tenant;
  const actions = tenantActions(me.role, view, me.scope);
  // A sandbox member reads the fleet but writes about sandbox tenants only
  // (D-477 ruling 3), so on a fleet tenant it is told why nothing is offered.
  const fleetOnly = me.scope === "sandbox" && !tenant.sandbox;
  const host = new URL(tenantOrigin(tenant.subdomain, window.location)).host;

  const headerActions: ReactNode[] = [];
  // M15-6: rename, defaults and the subdomain, for platform_admin on a tenant
  // this member may write about, while the tenant still exists.
  const editable =
    canEditTenant(me.role) &&
    !fleetOnly &&
    tenant.status !== "purging" &&
    tenant.status !== "purged";
  if (editable) {
    headerActions.push(
      <Button
        key="edit"
        size="sm"
        data-testid="admin-tenant-edit"
        onClick={() => {
          setDialog("edit");
        }}
      >
        {t("admin.tenantEdit.edit")}
      </Button>,
      <Button
        key="subdomain"
        size="sm"
        data-testid="admin-tenant-subdomain"
        onClick={() => {
          setDialog("subdomain");
        }}
      >
        {t("admin.tenantEdit.subdomain")}
      </Button>,
    );
  }
  if (actions.retry) {
    headerActions.push(
      <Button
        key="retry"
        variant="primary"
        size="sm"
        busy={retry.isPending}
        busyLabel={t("admin.tenant.retrying")}
        data-testid="admin-tenant-retry"
        onClick={() => {
          retry.mutate();
        }}
      >
        {t("admin.tenant.retry")}
      </Button>,
    );
  }
  if (actions.suspend) {
    headerActions.push(
      <Button
        key="suspend"
        variant="danger"
        size="sm"
        data-testid="admin-tenant-suspend"
        onClick={() => {
          setDialog("suspend");
        }}
      >
        {t("admin.tenant.suspend")}
      </Button>,
    );
  }
  if (actions.restore) {
    headerActions.push(
      <Button
        key="restore"
        variant="primary"
        size="sm"
        data-testid="admin-tenant-restore"
        onClick={() => {
          setDialog("restore");
        }}
      >
        {t("admin.tenant.restore")}
      </Button>,
    );
  }

  return (
    <section className="admin-screen" data-testid="admin-tenant-detail">
      <PageHeader
        title={tenant.name}
        crumbs={[{ label: t("admin.tenant.back"), to: "/console/tenants" }]}
        actions={
          <span className="admin-head-actions">
            {tenant.sandbox ? (
              <StatusChip variant="accent" dot={false} data-testid="admin-tenant-detail-sandbox">
                {t("admin.tenants.sandbox")}
              </StatusChip>
            ) : null}
            <StatusChip
              variant={statusVariant(tenant.status)}
              data-testid="admin-tenant-detail-status"
            >
              {enumLabel(t, "admin.status", tenant.status)}
            </StatusChip>
            {headerActions}
          </span>
        }
      />

      <TenantTabs tenantId={tenantId} />
      {fleetOnly ? (
        <Banner kind="neutral" data-testid="admin-tenant-sandbox-only">
          {t("admin.tenant.sandboxOnly")}
        </Banner>
      ) : null}
      {notice !== null ? (
        <Banner
          kind="ok"
          data-testid="admin-tenant-notice"
          onDismiss={() => {
            setNotice(null);
          }}
          dismissLabel={t("admin.suspend.close")}
        >
          {notice}
        </Banner>
      ) : null}
      {retry.isError ? (
        <Banner kind="bad" data-testid="admin-tenant-retry-error">
          {actionMessage(retry.error, t("admin.tenant.retryFailed"), t)}
        </Banner>
      ) : null}

      <div className="admin-detail-grid">
        <Card title={t("admin.tenant.overview")} data-testid="admin-tenant-overview">
          <Facts>
            <Fact label={t("admin.tenant.subdomain")}>
              <a
                href={tenantOrigin(tenant.subdomain, window.location)}
                target="_blank"
                rel="noreferrer"
                data-testid="admin-tenant-link"
              >
                {host}
              </a>
            </Fact>
            <Fact label={t("admin.health.colHealth")} data-testid="admin-tenant-health-fact">
              <span className="admin-inline">
                <HealthDot health={tenant.health} data-testid="admin-tenant-detail-health" />
                <Button
                  size="sm"
                  data-testid="admin-tenant-health-open"
                  onClick={() => {
                    void navigate({
                      to: "/console/tenants/$tenantId/health",
                      params: { tenantId: tenant.id },
                    });
                  }}
                >
                  {t("admin.health.open")}
                </Button>
              </span>
            </Fact>
            <Fact label={t("admin.tenant.pack")}>
              {packLabel(tenant.vertical_pack, packs.data, i18n.language)}
            </Fact>
            <Fact label={t("admin.tenantEdit.plan")} data-testid="admin-tenant-plan">
              {tenant.plan_name}
            </Fact>
            <Fact label={t("admin.tenant.region")}>
              {enumLabel(t, "admin.regions", tenant.region)}
            </Fact>
            <Fact label={t("admin.tenant.locale")}>{LOCALE_ENDONYMS[tenant.default_locale]}</Fact>
            <Fact label={t("admin.tenant.currency")}>{tenant.currency}</Fact>
            <Fact label={t("admin.tenant.timezone")}>{tenant.default_timezone}</Fact>
            <Fact label={t("admin.tenant.created")}>
              {formatTimestamp(tenant.created_at, i18n.language)}
            </Fact>
            <Fact label={t("admin.tenant.adminEmail")} data-testid="admin-tenant-admin">
              <span className="admin-inline">
                <span data-testid="admin-tenant-admin-email">{tenant.group_admin_email}</span>
                {actions.resendInvite ? (
                  <Button
                    size="sm"
                    busy={resend.isPending}
                    busyLabel={t("admin.tenant.resending")}
                    data-testid="admin-tenant-resend"
                    onClick={() => {
                      setNotice(null);
                      resend.mutate();
                    }}
                  >
                    {t("admin.tenant.resendInvite")}
                  </Button>
                ) : null}
              </span>
            </Fact>
          </Facts>
          {resend.isError ? (
            <Banner kind="bad" data-testid="admin-tenant-resend-error">
              {actionMessage(resend.error, t("admin.tenant.resendFailed"), t)}
            </Banner>
          ) : null}
        </Card>

        {view.suspension !== null ? (
          <Card title={t("admin.tenant.suspension")} data-testid="admin-tenant-suspension">
            <Facts>
              <Fact label={t("admin.tenant.suspendedFor")}>
                {enumLabel(t, "admin.reasons", view.suspension.reason)}
              </Fact>
              <Fact label={t("admin.tenant.suspendedMode")}>
                {t(`admin.modes.${view.suspension.mode}`)}
              </Fact>
              <Fact label={t("admin.tenant.suspendedAt")}>
                {formatTimestamp(view.suspension.suspended_at, i18n.language)}
              </Fact>
              {view.suspension.grace_ends_at !== null ? (
                <Fact label={t("admin.tenant.graceEnds")}>
                  {formatTimestamp(view.suspension.grace_ends_at, i18n.language)}
                </Fact>
              ) : null}
            </Facts>
          </Card>
        ) : null}

        {view.provisioning !== null &&
        (tenant.status === "provisioning" || tenant.status === "provision_failed") ? (
          <Card title={t("admin.tenant.setup")} data-testid="admin-tenant-setup">
            <ProvisioningSteps detail={view} />
            <Facts>
              <Fact label={t("admin.tenant.attempts")}>{view.provisioning.attempt_count}</Fact>
              {view.provisioning.last_error !== null ? (
                <Fact label={t("admin.tenant.lastError")}>
                  <code className="admin-wrap">{view.provisioning.last_error}</code>
                </Fact>
              ) : null}
            </Facts>
          </Card>
        ) : null}

        <LifecycleTimeline tenantId={tenantId} role={me.role} />
      </div>

      {dialog === "suspend" ? (
        <SuspendDialog
          detail={view}
          reasons={actions.suspendReasons}
          modes={actions.suspendModes}
          heldKey={suspendKey}
          onRefused={refused}
          onClose={() => {
            setDialog(null);
          }}
          onDone={(next) => {
            settle(next);
            setDialog(null);
          }}
        />
      ) : null}
      {dialog === "edit" ? (
        <EditTenantDialog
          detail={view}
          onClose={() => {
            setDialog(null);
          }}
          onDone={(next) => {
            settle(next);
            setDialog(null);
          }}
        />
      ) : null}
      {dialog === "subdomain" ? (
        <SubdomainDialog
          detail={view}
          hostFor={(sub) => new URL(tenantOrigin(sub, window.location)).host}
          onClose={() => {
            setDialog(null);
          }}
          onDone={(next) => {
            settle(next);
            setDialog(null);
          }}
        />
      ) : null}
      {dialog === "restore" ? (
        <RestoreDialog
          detail={view}
          heldKey={restoreKey}
          onRefused={refused}
          onClose={() => {
            setDialog(null);
          }}
          onDone={(next) => {
            settle(next);
            setDialog(null);
          }}
        />
      ) : null}
    </section>
  );
}

/** The provisioning worker's five steps, in order, with where each one is. */
export function ProvisioningSteps({ detail }: { detail: TenantDetail }) {
  const { t } = useTranslation();
  if (detail.provisioning === null) return null;
  return (
    <ol className="admin-steps" data-testid="admin-provisioning-steps">
      {detail.provisioning.steps.map((step) => (
        <li key={step.name} className="admin-step" data-step={step.name} data-state={step.status}>
          <span className="admin-step-name">{enumLabel(t, "admin.steps", step.name)}</span>
          <StatusChip
            variant={step.status === "done" ? "ok" : "neutral"}
            dot={step.status !== "pending"}
          >
            {t(`admin.stepState.${step.status}`)}
          </StatusChip>
        </li>
      ))}
    </ol>
  );
}

function LifecycleTimeline({ tenantId, role }: { tenantId: string; role: PlatformMe["role"] }) {
  const { t, i18n } = useTranslation();
  const events = useInfiniteQuery({
    queryKey: lifecycleQueryKey(tenantId),
    queryFn: ({ pageParam, signal }) => listLifecycleEvents(tenantId, pageParam, signal),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
  });
  // Staff names where this role may read the directory, "platform staff"
  // otherwise, never an id (hooks/use-actor-name.ts).
  const actorName = useActorName(role);
  function who(event: LifecycleEvent): string {
    return actorName(event.actor);
  }

  function statusName(status: string): string {
    return enumLabel(t, "admin.status", status);
  }

  let body: ReactNode;
  if (events.isPending) {
    body = <CardSkeleton lines={3} label={t("admin.tenant.history")} />;
  } else if (events.isError) {
    body = (
      <ErrorState
        title={t("admin.tenant.historyFailed")}
        retryLabel={t("admin.load.retry")}
        onRetry={() => {
          void events.refetch();
        }}
        requestId={requestIdOf(events.error)}
        requestIdLabel={t("admin.load.requestId")}
      />
    );
  } else {
    const items = events.data.pages.flatMap((p) => p.items);
    body =
      items.length === 0 ? (
        <p className="admin-muted">{t("admin.tenant.historyEmpty")}</p>
      ) : (
        <>
          <ol className="admin-timeline" data-testid="admin-tenant-timeline">
            {items.map((event) => (
              <li key={event.id} className="admin-timeline-row" data-to={event.to_status}>
                <span className="admin-timeline-what">
                  {event.from_status === "" || event.from_status === "none"
                    ? t("admin.tenant.createdEvent")
                    : `${statusName(event.from_status)} → ${statusName(event.to_status)}`}
                </span>
                {event.reason !== null && event.reason !== "" ? (
                  <span className="admin-muted">{enumLabel(t, "admin.reasons", event.reason)}</span>
                ) : null}
                <span className="admin-muted">
                  {formatTimestamp(event.occurred_at, i18n.language)} · {who(event)}
                </span>
              </li>
            ))}
          </ol>
          {events.hasNextPage ? (
            <Button
              size="sm"
              busy={events.isFetchingNextPage}
              data-testid="admin-tenant-history-more"
              onClick={() => {
                void events.fetchNextPage();
              }}
            >
              {t("admin.tenant.historyMore")}
            </Button>
          ) : null}
        </>
      );
  }

  return (
    <Card title={t("admin.tenant.history")} data-testid="admin-tenant-history">
      {body}
    </Card>
  );
}

function SuspendDialog({
  detail,
  reasons,
  modes,
  heldKey,
  onRefused,
  onClose,
  onDone,
}: {
  detail: TenantDetail;
  reasons: readonly SuspensionReason[];
  modes: readonly SuspensionMode[];
  heldKey: HeldKey;
  onRefused: (error: unknown) => void;
  onClose: () => void;
  onDone: (next: TenantDetail) => void;
}) {
  const { t } = useTranslation();
  const [reason, setReason] = useState<SuspensionReason>(reasons[0] ?? "billing_overdue");
  const [mode, setMode] = useState<SuspensionMode>(modes[0] ?? "grace");
  const [note, setNote] = useState("");
  const suspend = useMutation({
    mutationFn: () => {
      const body = { reason, mode, ...(note.trim() !== "" ? { note: note.trim() } : {}) };
      return withHeldKey(heldKey, { suspend: detail.tenant.id, body }, (key) =>
        suspendTenant(detail.tenant.id, body, key),
      );
    },
    onSuccess: onDone,
    onError: onRefused,
  });
  useSignInAgainOn401(suspend.error);
  return (
    <Modal
      label={t("admin.suspend.title", { name: detail.tenant.name })}
      heading={t("admin.suspend.title", { name: detail.tenant.name })}
      closeLabel={t("admin.suspend.close")}
      onClose={onClose}
      pending={suspend.isPending}
      testId="admin-suspend-dialog"
      className="admin-modal"
    >
      <form
        className="admin-form"
        onSubmit={(e) => {
          e.preventDefault();
          suspend.mutate();
        }}
      >
        <Field label={t("admin.suspend.reason")}>
          <Select
            value={reason}
            data-testid="admin-suspend-reason"
            onChange={(e) => {
              setReason(e.target.value as SuspensionReason);
            }}
          >
            {reasons.map((r) => (
              <option key={r} value={r}>
                {t(`admin.reasons.${r}`)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t("admin.suspend.mode")}>
          <Select
            value={mode}
            data-testid="admin-suspend-mode"
            onChange={(e) => {
              setMode(e.target.value as SuspensionMode);
            }}
          >
            {modes.map((m) => (
              <option key={m} value={m}>
                {t(`admin.modes.${m}`)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t("admin.suspend.note")}>
          <textarea
            className="ds-input"
            rows={3}
            maxLength={2000}
            value={note}
            onChange={(e) => {
              setNote(e.target.value);
            }}
          />
        </Field>
        {suspend.isError ? (
          <Banner kind="bad" data-testid="admin-suspend-error">
            {actionMessage(suspend.error, t("admin.suspend.failed"), t)}
          </Banner>
        ) : null}
        <div className="admin-actions">
          <Button
            type="submit"
            variant="danger"
            busy={suspend.isPending}
            busyLabel={t("admin.suspend.working")}
            data-testid="admin-suspend-confirm"
          >
            {t("admin.suspend.confirm")}
          </Button>
          <Button disabled={suspend.isPending} onClick={onClose}>
            {t("admin.suspend.cancel")}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function RestoreDialog({
  detail,
  heldKey,
  onRefused,
  onClose,
  onDone,
}: {
  detail: TenantDetail;
  heldKey: HeldKey;
  onRefused: (error: unknown) => void;
  onClose: () => void;
  onDone: (next: TenantDetail) => void;
}) {
  const { t } = useTranslation();
  const [note, setNote] = useState("");
  const restore = useMutation({
    mutationFn: () => {
      const body = note.trim() === "" ? {} : { note: note.trim() };
      return withHeldKey(heldKey, { restore: detail.tenant.id, body }, (key) =>
        restoreTenant(detail.tenant.id, body, key),
      );
    },
    onSuccess: onDone,
    onError: onRefused,
  });
  useSignInAgainOn401(restore.error);
  return (
    <Modal
      label={t("admin.restore.title", { name: detail.tenant.name })}
      heading={t("admin.restore.title", { name: detail.tenant.name })}
      closeLabel={t("admin.restore.close")}
      onClose={onClose}
      pending={restore.isPending}
      testId="admin-restore-dialog"
      className="admin-modal"
    >
      <form
        className="admin-form"
        onSubmit={(e) => {
          e.preventDefault();
          restore.mutate();
        }}
      >
        <p className="admin-muted">{t("admin.restore.body")}</p>
        <Field label={t("admin.restore.note")}>
          <textarea
            className="ds-input"
            rows={3}
            maxLength={2000}
            value={note}
            onChange={(e) => {
              setNote(e.target.value);
            }}
          />
        </Field>
        {restore.isError ? (
          <Banner kind="bad" data-testid="admin-restore-error">
            {actionMessage(restore.error, t("admin.restore.failed"), t)}
          </Banner>
        ) : null}
        <div className="admin-actions">
          <Button
            type="submit"
            variant="primary"
            busy={restore.isPending}
            busyLabel={t("admin.restore.working")}
            data-testid="admin-restore-confirm"
          >
            {t("admin.restore.confirm")}
          </Button>
          <Button disabled={restore.isPending} onClick={onClose}>
            {t("admin.restore.cancel")}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
